import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Bell,
  Sun,
  Moon,
  LogOut,
  Menu,
  ChevronDown,
} from "lucide-react";
import { useAuthContext } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

const TITLES = [
  ["/dashboard", "Dashboard"],
  ["/doctors", "Doctors"],
  ["/patients", "Patients"],
  ["/consultations", "Consultations"],
  ["/transactions", "Transactions"],
  ["/withdrawals", "Withdrawals"],
  ["/coupons", "Coupons"],
  ["/notifications", "Notifications"],
  ["/reports", "Reports"],
  ["/users", "User details"],
];

const HeaderShadcn = ({ onMenuClick, isMobile }) => {
  const { pathname } = useLocation();
  const pageTitle = (TITLES.find(([p]) => pathname === p || pathname.startsWith(p + "/")) || [])[1] || "Admin";
  const { user, logout } = useAuthContext();
  const { theme, toggleTheme } = useTheme();
  const [showNotifications, setShowNotifications] = useState(false);

  const handleLogout = async () => {
    await logout();
  };

  const notifications = [
    { id: 1, title: "New doctor registered", time: "2 min ago", unread: true, color: "#3b82f6" },
    { id: 2, title: "Withdrawal request pending", time: "15 min ago", unread: true, color: "#f59e0b" },
    { id: 3, title: "Patient consultation completed", time: "1 hour ago", unread: false, color: "#10b981" },
    { id: 4, title: "System maintenance scheduled", time: "2 hours ago", unread: false, color: "#6366f1" },
  ];

  const unreadCount = notifications.filter((n) => n.unread).length;

  return (
    <header className="glass-header sticky top-0 z-30">
      <div className="flex h-16 items-center justify-between gap-4 px-4 sm:px-6">
        {/* Left */}
        <div className="flex items-center gap-3">
          {isMobile && (
            <button
              onClick={onMenuClick}
              aria-label="Open menu"
              className="flex h-9 w-9 items-center justify-center rounded-lg border bg-card text-foreground hover:bg-muted"
            >
              <Menu size={18} />
            </button>
          )}
          <h1 className="text-lg font-semibold tracking-tight text-foreground">{pageTitle}</h1>
        </div>

        <div className="flex-1" />

        {/* Right */}
        <div className="flex items-center gap-2">
          <button
            onClick={toggleTheme}
            aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
            className="flex h-10 w-10 items-center justify-center rounded-lg border bg-card text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
          </button>
          <Popover open={showNotifications} onOpenChange={setShowNotifications}>
            <PopoverTrigger asChild>
              <button
                aria-label="Notifications"
                className="relative flex h-10 w-10 items-center justify-center rounded-lg border bg-card text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <Bell size={17} />
                {unreadCount > 0 && (
                  <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-red-500 ring-2 ring-white" />
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="end">
              <div className="flex items-center justify-between border-b px-4 py-3">
                <span className="text-sm font-semibold">Notifications</span>
                <button
                  onClick={() => setShowNotifications(false)}
                  className="text-xs font-semibold text-primary hover:underline"
                >
                  Mark all read
                </button>
              </div>
              <div className="max-h-72 overflow-y-auto">
                {notifications.map((n) => (
                  <div
                    key={n.id}
                    className={`flex cursor-pointer items-start gap-3 border-b px-4 py-3 last:border-0 hover:bg-muted/60 ${n.unread ? "bg-accent/40" : ""}`}
                  >
                    <span
                      className="mt-1.5 h-2 w-2 shrink-0 rounded-full"
                      style={{ background: n.unread ? n.color : "hsl(var(--border))" }}
                    />
                    <div className="flex-1">
                      <p className={`text-[13px] ${n.unread ? "font-semibold" : "font-medium"}`}>{n.title}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">{n.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-lg border bg-card py-1 pl-1 pr-2.5 hover:bg-muted">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.photoURL} />
                  <AvatarFallback className="bg-primary text-xs font-semibold text-primary-foreground">
                    {user?.displayName?.[0] || user?.email?.[0]?.toUpperCase() || "A"}
                  </AvatarFallback>
                </Avatar>
                {!isMobile && (
                  <div className="text-left leading-tight">
                    <p className="text-[13px] font-semibold">
                      {user?.displayName || user?.email?.split("@")[0]}
                    </p>
                    <p className="text-[11px] text-muted-foreground">Administrator</p>
                  </div>
                )}
                <ChevronDown size={14} className="text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuLabel>
                <p className="truncate text-sm font-semibold">
                  {user?.displayName || user?.email?.split("@")[0]}
                </p>
                <p className="truncate text-xs font-normal text-muted-foreground">{user?.email}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600 focus:text-red-600">
                <LogOut size={14} className="mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default HeaderShadcn;
